import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

// Layout components
import Layout from '../../../components/layout/Layout';
import Section from '../../../components/layout/Section';
import Container from '../../../components/layout/Container';

// UI Components
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import FeatureCard from '../../../components/ui/FeatureCard';
import { FAQList } from '../../../components/ui/FAQ';
import Hero from '../../../components/sections/Hero';
import CallToAction from '../../../components/sections/CallToAction';

// Data
import { caseStudies, faqs } from '../../../data/mockData';

/**
 * Healthcare Industry Solutions Page
 */
const HealthcareIndustryPage: React.FC = () => {
  // Filter relevant case studies for the healthcare industry
  const relevantCaseStudies = caseStudies.filter(
    (study) => study.industry === 'Healthcare'
  ).slice(0, 2);

  // Filter relevant FAQs
  const relevantFaqs = faqs.slice(0, 3);

  return (
    <Layout>
      <Hero
        title="AI Solutions for Healthcare"
        subtitle="Optimize healthcare delivery, improve clinical outcomes, and streamline administrative processes with our secure AI solutions."
        ctaText="Schedule a Consultation"
        ctaLink="/contact"
        backgroundType="gradient"
      />

      {/* Key Challenges Section */}
      <Section bgColor="bg-white" padding="lg">
        <Container>
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Industry Challenges We Solve</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <Card>
                <h3 className="text-xl font-semibold mb-3">Administrative Burden</h3>
                <p className="text-neutral-700 mb-4">
                  Healthcare professionals spend too much time on paperwork and administrative tasks.
                  Our AI tools automate routine processes, freeing up valuable time for patient care.
                </p>
              </Card>
              
              <Card>
                <h3 className="text-xl font-semibold mb-3">Scheduling Inefficiencies</h3>
                <p className="text-neutral-700 mb-4">
                  Optimizing schedules for patients, staff, and resources is incredibly complex.
                  Our AI scheduling systems balance multiple constraints while reducing wait times and maximizing resource utilization.
                </p>
              </Card>
              
              <Card>
                <h3 className="text-xl font-semibold mb-3">Clinical Decision Support</h3>
                <p className="text-neutral-700 mb-4">
                  Providers need quick access to relevant information during patient encounters.
                  Our AI solutions analyze patient data to provide tailored, evidence-based recommendations at the point of care.
                </p>
              </Card>
              
              <Card>
                <h3 className="text-xl font-semibold mb-3">Regulatory Compliance</h3>
                <p className="text-neutral-700 mb-4">
                  Maintaining compliance with healthcare regulations is challenging and resource-intensive.
                  Our AI compliance tools automate monitoring and documentation to reduce risk and administrative burden.
                </p>
              </Card>
            </div>
          </div>
        </Container>
      </Section>
      
      {/* Our Solutions Section */}
      <Section bgColor="bg-neutral-50" padding="lg">
        <Container>
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Our Healthcare Solutions</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <FeatureCard
                title="Patient Scheduling Optimization"
                description="AI algorithms that reduce wait times by 35% while accommodating patient preferences and clinical constraints."
                icon={
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                }
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <FeatureCard
                title="Medical Record Analysis"
                description="NLP systems that extract key clinical information from medical records to provide comprehensive patient insights."
                icon={
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                }
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <FeatureCard
                title="Predictive Care Management"
                description="Machine learning models that identify patients at risk for complications, enabling proactive interventions."
                icon={
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                }
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <FeatureCard
                title="Administrative Automation"
                description="Workflow solutions that automate documentation, coding, and billing processes, reducing administrative time by up to 70%."
                icon={
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                }
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <FeatureCard
                title="Compliance Monitoring"
                description="AI systems that continuously monitor operations for regulatory compliance, automatically documenting and alerting when needed."
                icon={
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                }
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <FeatureCard
                title="Clinical Decision Support"
                description="AI-powered tools that provide evidence-based recommendations to clinicians at the point of care, improving outcomes and reducing errors."
                icon={
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                }
              />
            </motion.div>
            </div>

            <div className="text-center">
              <Button
                variant="primary"
                size="lg"
                onClick={() => window.location.href = '/services'}
              >
                Explore All Our Services
              </Button>
            </div>
          </div>
        </Container>
      </Section>
      
      {/* Case Studies Section */}
      {relevantCaseStudies.length > 0 && (
        <Section bgColor="bg-white" padding="lg">
          <Container>
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">Success Stories in Healthcare</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {relevantCaseStudies.map((study, index) => (
                  <motion.div
                    key={study.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex"
                  >
                    <Link to={study.url} className="w-full h-full">
                      <Card hover className="h-full flex flex-col">
                        <div className="aspect-video bg-neutral-200 rounded-t-lg overflow-hidden">
                          <img
                            src={study.imageSrc}
                            alt={study.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const img = e.target as HTMLImageElement;
                              img.style.display = 'none';
                              if (img.nextElementSibling) {
                                (img.nextElementSibling as HTMLElement).style.display = 'flex';
                              }
                            }}
                          />
                          <div
                            className="w-full h-full bg-gradient-to-br from-primary to-secondary opacity-70 flex items-center justify-center hidden"
                            style={{ display: 'flex' }}
                          >
                            <span className="text-white text-lg font-semibold">{study.industry}</span>
                          </div>
                        </div>

                        <div className="p-6 md:p-8 flex-grow flex flex-col">
                          <div className="mb-3">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                              {study.solution}
                            </span>
                          </div>

                          <h3 className="text-xl font-semibold mb-3">{study.title}</h3>
                          <p className="text-neutral-600 mb-4 text-sm">{study.excerpt}</p>

                          <div className="mt-auto pt-2 border-t border-neutral-100">
                            <span className="inline-flex items-center text-primary font-medium text-sm">
                              Read full case study
                              <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                              </svg>
                            </span>
                          </div>
                        </div>
                      </Card>
                    </Link>
                  </motion.div>
                ))}
              </div>
            
              <div className="text-center mt-10">
                <Link to="/case-studies" className="inline-flex items-center text-primary font-medium">
                  View all case studies
                  <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </Link>
              </div>
            </div>
          </Container>
        </Section>
      )}
      
      {/* FAQ Section */}
      <Section bgColor="bg-neutral-50" padding="lg">
        <Container>
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
            <FAQList faqs={relevantFaqs} />
            
            <div className="mt-10 text-center">
              <Link to="/contact" className="inline-flex items-center text-primary font-medium">
                Have more questions? Contact our healthcare AI specialists
                <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </div>
          </div>
        </Container>
      </Section>
      
      {/* Call to Action */}
      <CallToAction
        title="Ready to transform healthcare delivery with AI?"
        description="Schedule a consultation to discuss how our AI solutions can help your healthcare organization improve patient care and operational efficiency."
        ctaText="Schedule a Consultation"
        ctaLink="/contact"
      />
    </Layout>
  );
};

export default HealthcareIndustryPage;